import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals } from 'utils/formats';
import Button from 'modules/_shared/components/Buttons';
import { useEquityMarket } from '../contexts/EquityMarketContext';

type KpiDetailPair = {
  label: string;
  value: string | number;
};

type KpiDetailLine = {
  pairs: KpiDetailPair[];
  highlight?: {
    variant: 'success' | 'danger';
    icon?: string;
  };
};

type KpiCardProps = {
  icon: string;
  title: string;
  actionLabel: string;
  actionDisabled?: boolean;
  onAction: () => void;
  amount: string | number;
  amountLabel: string;
  details: KpiDetailLine[];
};

const KpiCard: FC<KpiCardProps> = ({
  icon,
  title,
  actionLabel,
  actionDisabled = false,
  onAction,
  amount,
  amountLabel,
  details,
}) => (
  <div className="card card-bordered card-full h-100">
    <div className="card-inner d-flex flex-column h-100">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center g-2">
          <div className="icon-circle icon-circle-lg bg-primary-dim text-primary me-3">
            <em className={`icon ni ${icon}`} />
          </div>
          <h6 className="card-title text-base mb-0">{title}</h6>
        </div>

        <Button
          variant="link"
          className="link link-primary btn btn-link p-0"
          onClick={onAction}
          disabled={actionDisabled}
        >
          {actionLabel}
        </Button>
      </div>

      <div className="card-amount mb-2">
        <span className="amount">{amount}</span>
        <span className="sub-text ml-1">{amountLabel}</span>
      </div>

      <hr className="w-100 border border-primary mt-0" />

      {details.map((line) => (
        <div
          key={line.pairs.map((p) => p.label).join('-')}
          className={`mt-1 ${
            line.highlight
              ? `text-${line.highlight.variant} d-flex align-items-center`
              : 'text-soft'
          }`}
        >
          {line.highlight?.icon && (
            <em className={`icon ni ${line.highlight.icon} mr-1`} />
          )}
          {line.pairs.map((pair, pairIndex) => (
            <span key={pair.label}>
              {pairIndex > 0 && (
                <span className="text-soft">&nbsp;·&nbsp;</span>
              )}
              <span className={line.highlight ? '' : 'text-soft'}>
                {pair.label}:&nbsp;
              </span>
              <span className={line.highlight ? '' : 'text-dark fw-medium'}>
                {pair.value}
              </span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const EquityMarketKpiCards: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useEquityMarket();

  return (
    <div className="nk-block nk-block-spacing">
      <div className="row gy-gs">
        {/* My Shares Card */}
        <div className="col-12 col-md-6 col-lg-4">
          <KpiCard
            icon="ni-layers"
            title={t('MyShares')}
            actionLabel={t('ViewDetail')}
            actionDisabled
            onAction={actions.handleViewMyShares}
            amount={data.partnerShares.total.toLocaleString()}
            amountLabel={t('shares')}
            details={[
              {
                pairs: [
                  {
                    label: t('AcquisitionValue'),
                    value: formatCurrencyDecimals(
                      data.partnerShares.acquisitionValue
                    ),
                  },
                ],
              },
              {
                pairs: [
                  {
                    label: t('AvgAcquisitionPrice'),
                    value: `${formatCurrencyDecimals(
                      data.partnerShares.avgAcquisitionPrice
                    )} /${t('Share').toLowerCase()}`,
                  },
                ],
              },
              {
                pairs: [
                  {
                    label: t('EstimatedCurrentValue'),
                    value: `${formatCurrencyDecimals(
                      data.partnerShares.estimatedValue
                    )} (+${data.partnerShares.valueChange}%)`,
                  },
                ],
                highlight: { variant: 'success', icon: 'ni-arrow-up' },
              },
            ]}
          />
        </div>

        {/* My Active Offers Card */}
        <div className="col-12 col-md-6 col-lg-4">
          <KpiCard
            icon="ni-offer"
            title={t('MyActiveOffers')}
            actionLabel={t('ViewOffers')}
            onAction={actions.handleViewMyOffers}
            amount={data.partnerActiveOffers.total}
            amountLabel={t('ActiveOffers').toLowerCase()}
            details={[
              {
                pairs: [
                  {
                    label: t('Sales'),
                    value: data.partnerActiveOffers.sellCount,
                  },
                  {
                    label: t('Purchases'),
                    value: data.partnerActiveOffers.buyCount,
                  },
                ],
              },
              {
                pairs: [
                  {
                    label: t('Sale'),
                    value: formatCurrencyDecimals(
                      data.partnerActiveOffers.sellAmount
                    ),
                  },
                  {
                    label: t('Purchase'),
                    value: formatCurrencyDecimals(
                      data.partnerActiveOffers.buyAmount
                    ),
                  },
                ],
              },
            ]}
          />
        </div>

        {/* Current Market Card */}
        <div className="col-12 col-md-6 col-lg-4">
          <KpiCard
            icon="ni-exchange"
            title={t('CurrentMarket')}
            actionLabel={t('ExploreMarket')}
            actionDisabled
            onAction={actions.handleViewSellOffers}
            amount={data.marketInfo.totalOffers}
            amountLabel={t('AvailableOffers').toLowerCase()}
            details={[
              {
                pairs: [
                  { label: t('Sales'), value: data.marketInfo.sellCount },
                  { label: t('Purchases'), value: data.marketInfo.buyCount },
                ],
              },
              {
                pairs: [
                  {
                    label: t('Sale'),
                    value: formatCurrencyDecimals(data.marketInfo.sellAmount),
                  },
                  {
                    label: t('Purchase'),
                    value: formatCurrencyDecimals(data.marketInfo.buyAmount),
                  },
                ],
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default EquityMarketKpiCards;
